// src/features/pwa/offline-query.ts
import { QueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'

// Типы для IndexedDB кэша
interface CachedQuery {
  queryKey: unknown[]
  data: unknown
  dataUpdatedAt: number
}

interface ApiError {
  response?: {
    status: number
  }
}

const CACHE_KEY = 'react-query-offline-cache'

// Простая реализация IndexedDB без внешних зависимостей
const idb = {
  async get(key: string): Promise<CachedQuery[] | undefined> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWA-Cache', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['cache'], 'readonly')
        const store = transaction.objectStore('cache')
        const getRequest = store.get(key)
        
        getRequest.onsuccess = () => resolve(getRequest.result)
        getRequest.onerror = () => reject(getRequest.error)
      }
      
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache')
        }
      }
    })
  },
  
  async set(key: string, value: CachedQuery[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWA-Cache', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['cache'], 'readwrite')
        const store = transaction.objectStore('cache')
        const putRequest = store.put(value, key)
        
        putRequest.onsuccess = () => resolve()
        putRequest.onerror = () => reject(putRequest.error)
      }
      
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache')
        }
      }
    })
  },
  
  async del(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWA-Cache', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['cache'], 'readwrite')
        const store = transaction.objectStore('cache')
        const deleteRequest = store.delete(key)
        
        deleteRequest.onsuccess = () => resolve()
        deleteRequest.onerror = () => reject(deleteRequest.error)
      }
    })
  },
  
  async keys(): Promise<IDBValidKey[]> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWA-Cache', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['cache'], 'readonly')
        const store = transaction.objectStore('cache')
        const getAllKeysRequest = store.getAllKeys()
        
        getAllKeysRequest.onsuccess = () => resolve(getAllKeysRequest.result)
        getAllKeysRequest.onerror = () => reject(getAllKeysRequest.error)
      }
    })
  }
}

// Персистентный QueryClient с офлайн-поддержкой
export const createPersistentQueryClient = (): QueryClient => {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 минут
        gcTime: 24 * 60 * 60 * 1000, // 24 часа (Garbage Collection)
        retry: (failureCount: number, error: unknown): boolean => {
          // Не ретраим при 401/403
          const apiError = error as ApiError
          if (apiError?.response?.status === 401) return false
          return failureCount < 3
        },
        retryDelay: (attemptIndex: number): number => 
          Math.min(1000 * 2 ** attemptIndex, 30000),
        networkMode: 'offlineFirst' // Критично для PWA
      }
    }
  })

  // Синхронизация с IndexedDB
  const persistCache = async (): Promise<void> => {
    const cache = client.getQueryCache()
    const queries = cache.getAll()
    
    const persistentQueries: CachedQuery[] = queries
      .filter(q => q.state.status === 'success')
      .map(q => ({
        queryKey: Array.isArray(q.queryKey) ? q.queryKey : [q.queryKey],
        data: q.state.data,
        dataUpdatedAt: q.state.dataUpdatedAt
      }))
    
    await idb.set(CACHE_KEY, persistentQueries)
  }

  // Восстановление из IndexedDB
  const restoreCache = async (): Promise<void> => {
    try {
      const cached = await idb.get(CACHE_KEY)
      if (!cached) return

      cached.forEach((item: CachedQuery) => {
        client.setQueryData(item.queryKey, item.data, {
          updatedAt: item.dataUpdatedAt
        })
      })
    } catch (error) {
      console.error('Failed to restore cache:', error)
    }
  }

  // Авто-сохранение при изменениях
  client.getQueryCache().subscribe(() => {
    void persistCache()
  })

  // Восстановление при старте
  void restoreCache()

  // Очистка старых данных при превышении лимита (100MB)
  const cleanupOldCache = async (): Promise<void> => {
    const allKeys = await idb.keys()
    const queryKeys = allKeys.filter((k: IDBValidKey) => 
      typeof k === 'string' && k.startsWith('react-query')
    )
    
    if (queryKeys.length > 100) {
      const toDelete = queryKeys.slice(0, queryKeys.length - 100)
      await Promise.all(toDelete.map((k: IDBValidKey) => {
        if (typeof k === 'string') {
          return idb.del(k)
        }
        return Promise.resolve()
      }))
    }
  }

  void cleanupOldCache()

  return client
}

// Хук для отслеживания синхронизации
export const useSyncStatus = (): number => {
  const [pendingMutations, setPendingMutations] = useState<number>(0)

  useEffect(() => {
    const checkPending = async (): Promise<void> => {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        if ('sync' in registration) {
          const tags: string[] = await registration.sync.getTags()
          setPendingMutations(tags.filter((t: string) => t === 'executive-queue').length)
        }
      }
    }

    void checkPending()
    const interval = setInterval(() => {
      void checkPending()
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return pendingMutations
}